import { createServerFn } from "@tanstack/react-start";
import { createAuthenticatedApiConfig, throwApiError } from "@/lib/utils.server";
import {
  getEmails,
  createEmail,
  deleteEmail,
  getEmail,
  updateEmail,
  organisationGetEmails,
  organisationCreateEmail,
  organisationDeleteEmail,
  organisationGetEmail,
  organisationUpdateEmail,
} from "@/lib/mma/sdk.gen";
import type { EmailTemplate, EmailType, EmailLanguage } from "@/lib/mma/types.gen";
import { isForbiddenError } from "@/lib/utils";

// Get all system emails
export const getSystemEmailsFn = createServerFn({ method: "GET" }).handler(async () => {
  const config = await createAuthenticatedApiConfig();
  const response = await getEmails(config);
  const systemEmails: EmailTemplate[] = response?.data?.data || [];

  const uniqueEmailsMap = new Map<string, EmailTemplate>();

  systemEmails.forEach((email: any) => {
    const emailType = email?.type || email?.emailType;
    const emailLang = email?.language || email?.lang;

    if (emailType && emailLang) {
      const key = `${emailType}-${emailLang}`;
      if (!uniqueEmailsMap.has(key)) {
        uniqueEmailsMap.set(key, {
          emailType: emailType as EmailType,
          language: emailLang as EmailLanguage,
          subject: email?.subject,
          body: email?.body,
        });
      }
    }
  });

  const uniqueEmails = Array.from(uniqueEmailsMap.values());

  const result = uniqueEmails.map((email) => ({
    id: email.emailType + "-" + email.language,
    name: email.subject ?? email.emailType ?? "",
    emailType: email.emailType,
    language: email.language,
    href: `/emails/system?emailType=${email.emailType}&language=${email.language}`,
    dropdown: [
      {
        id: `${email.emailType + "-" + email.language}-delete`,
        name: "Delete",
        onClick: "delete",
        color: "red-500",
        parent: { name: email.subject, id: email.emailType + "-" + email.language },
      },
    ],
  }));

  return result;
});

// Get all organisation emails
export const getOrganisationEmailsFn = createServerFn({ method: "GET" })
  .inputValidator((data: { orgId: number }) => data)
  .handler(async ({ data }) => {
    const config = await createAuthenticatedApiConfig();
    const response = await organisationGetEmails({
      ...config,
      path: { id: data.orgId },
    });
    const organisationEmails: EmailTemplate[] = response?.data?.data || [];

    const uniqueEmailsMap = new Map<string, EmailTemplate>();

    organisationEmails.forEach((email: any) => {
      const emailType = email?.type || email?.emailType;
      const emailLang = email?.language || email?.lang;

      if (emailType && emailLang) {
        const key = `${emailType}-${emailLang}`;
        if (!uniqueEmailsMap.has(key)) {
          uniqueEmailsMap.set(key, {
            emailType: emailType as EmailType,
            language: emailLang as EmailLanguage,
            subject: email?.subject,
            body: email?.body,
          });
        }
      }
    });

    const uniqueEmails = Array.from(uniqueEmailsMap.values());

    return uniqueEmails.map((email) => {
      const key = email.emailType && email.language ? `${email.emailType}-${email.language}` : `email-${Math.random()}`;
      return {
        id: key,
        name: email.subject ?? email.emailType ?? "",
        emailType: email.emailType,
        language: email.language,
        href: `/emails/organisations/${data.orgId}?emailType=${email.emailType}&language=${email.language}`,
        dropdown: [
          {
            id: `${data.orgId}-${key}-delete`,
            name: "Delete",
            onClick: "delete",
            color: "red-500",
            parent: { name: email.subject, id: `${data.orgId}-${key}` },
          },
        ],
      };
    });
  });

// Get a specific system email
export const getSystemEmailFn = createServerFn({ method: "GET" })
  .inputValidator((data: { emailType: EmailType; language: EmailLanguage }) => data)
  .handler(async ({ data }) => {
    const config = await createAuthenticatedApiConfig();
    const response = await getEmail({
      ...config,
      path: {
        emailType: data.emailType,
        language: data.language,
      },
    });
    if (!response.data) {
      console.error("[getSystemEmailFn] No data in response");
      return throwApiError(response, "Failed to get system email", response.response?.status || 500);
    }
    const emailData = response.data?.data;
    return emailData;
  });

// Get a specific organisation email
export const getOrganisationEmailFn = createServerFn({ method: "GET" })
  .inputValidator((data: { orgId: number; emailType: EmailType; language: EmailLanguage }) => data)
  .handler(async ({ data }) => {
    const config = await createAuthenticatedApiConfig();
    const response = await organisationGetEmail({
      ...config,
      path: {
        id: data.orgId,
        emailType: data.emailType,
        language: data.language,
      },
    });
    if (!response.data) {
      console.error("[getOrganisationEmailFn] No data in response");
      return throwApiError(response, "Failed to get organisation email", response.response?.status || 500);
    }
    const emailData = response.data?.data;
    return emailData;
  });

// Create a new system email
export const createSystemEmailFn = createServerFn({ method: "POST" })
  .inputValidator((data: { type: EmailType; language: EmailLanguage; subject?: string; body?: string }) => data)
  .handler(async ({ data }) => {
    const config = await createAuthenticatedApiConfig();

    const response = await createEmail({
      ...config,
      body: {
        emailType: data.type,
        language: data.language,
        subject: data.subject,
        body: data.body,
      },
    });

    if (!response.data) {
      return throwApiError(response, "Failed to create system email", response.response.status || 500);
    }

    return response.data?.data;
  });

// Create a new organisation email
export const createOrganisationEmailFn = createServerFn({ method: "POST" })
  .inputValidator(
    (data: { orgId: number; type: EmailType; language: EmailLanguage; subject?: string; body?: string }) => data
  )
  .handler(async ({ data }) => {
    const config = await createAuthenticatedApiConfig();
    const response = await organisationCreateEmail({
      ...config,
      path: { id: data.orgId },
      body: {
        emailType: data.type,
        language: data.language,
        subject: data.subject,
        body: data.body,
      },
    });
    if (!response.data) {
      return throwApiError(response, "Failed to create organisation email", response.response?.status ?? 500);
    }
    return response.data?.data;
  });

// Update a system email
export const updateSystemEmailFn = createServerFn({ method: "POST" })
  .inputValidator((data: { emailType: EmailType; language: EmailLanguage; subject?: string; body?: string }) => data)
  .handler(async ({ data }) => {
    const config = await createAuthenticatedApiConfig();
    const response = await updateEmail({
      ...config,
      path: {
        emailType: data.emailType,
        language: data.language,
      },
      body: {
        subject: data.subject,
        body: data.body,
      },
    });
    if (!response.data) {
      return throwApiError(response, "Failed to update system email", response.response?.status ?? 500);
    }
    return response.data?.data || null;
  });

// Update an organisation email
export const updateOrganisationEmailFn = createServerFn({ method: "POST" })
  .inputValidator(
    (data: { orgId: number; emailType: EmailType; language: EmailLanguage; subject?: string; body?: string }) => data
  )
  .handler(async ({ data }) => {
    const config = await createAuthenticatedApiConfig();
    const response = await organisationUpdateEmail({
      ...config,
      path: {
        id: data.orgId,
        emailType: data.emailType,
        language: data.language,
      },
      body: {
        subject: data.subject,
        body: data.body,
      },
    });
    if (!response.data) {
      return throwApiError(response, "Failed to update organisation email", response.response?.status ?? 500);
    }
    return response.data?.data || null;
  });

// Delete a system email
export const deleteSystemEmailFn = createServerFn({ method: "POST" })
  .inputValidator((data: { emailType: EmailType; language: EmailLanguage }) => data)
  .handler(async ({ data }): Promise<{ success: boolean }> => {
    const config = await createAuthenticatedApiConfig();
    try {
      const response = await deleteEmail({
        ...config,
        path: {
          emailType: data.emailType,
          language: data.language,
        },
      });

      // Treat 404 as success - the email doesn't exist, which is what we want
      if (response.response?.status === 404) {
        return { success: true };
      }

      if (!response.data) {
        return throwApiError(response, "Failed to delete system email", response.response?.status || 500);
      }
      return { success: true };
    } catch (err: any) {
      // Check if it's a 404 error from the API call
      if (err.status === 404) {
        return { success: true };
      }
      console.error("🗑️ [SERVER] Delete exception:", err);
      throw err;
    }
  });

// Delete an organisation email
export const deleteOrganisationEmailFn = createServerFn({ method: "POST" })
  .inputValidator((data: { orgId: number; emailType: EmailType; language: EmailLanguage }) => data)
  .handler(async ({ data }): Promise<{ success: boolean }> => {
    const config = await createAuthenticatedApiConfig();
    try {
      const response = await organisationDeleteEmail({
        ...config,
        path: {
          id: data.orgId,
          emailType: data.emailType,
          language: data.language,
        },
      });

      // Treat 404 as success - the email doesn't exist, which is what we want
      if (response.response?.status === 404) {
        return { success: true };
      }

      if (!response.data) {
        return throwApiError(response, "Failed to delete organisation email", response.response?.status || 500);
      }
      return { success: true };
    } catch (err: any) {
      // Check if it's a 404 error from the API call
      if (err.status === 404) {
        return { success: true };
      }
      console.error("🗑️ [SERVER] Delete exception:", err);
      throw err;
    }
  });

// Get email types with their placeholders
export const getEmailTypesFn = createServerFn({ method: "GET" }).handler(async () => {
  const config = await createAuthenticatedApiConfig();

  const response = await fetch(`${config.baseUrl}/management/v1/email-types`, {
    headers: config.headers as HeadersInit,
  });

  if (!response.ok) {
    const error = new Error(`Failed to get email types: ${response.statusText}`);
    throw error;
  }

  const data = (await response.json()) as { data: Array<{ emailType: EmailType; placeholders: string[] }> };
  return data.data || [];
});

export const systemEmailsQueryOptions = {
  queryKey: ["emails", "system"],
  queryFn: async () => getSystemEmailsFn(),
  staleTime: 1000 * 60 * 5,
  gcTime: 1000 * 60 * 60,
  refetchOnWindowFocus: false,
  refetchOnMount: (query: any) => (isForbiddenError(query.state.error) ? false : "always"),
  refetchOnReconnect: false,
};

export function organisationEmailsQueryOptions(orgId: number) {
  return {
    queryKey: ["emails", "organisation", orgId],
    queryFn: () => getOrganisationEmailsFn({ data: { orgId } }),
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 60,
    refetchOnWindowFocus: false,
    refetchOnMount: (query: any) => (isForbiddenError(query.state.error) ? false : "always"),
    refetchOnReconnect: false,
  };
}

export function systemEmailQueryOptions(type: EmailType, language: EmailLanguage) {
  return {
    queryKey: ["email", "system", type, language],
    queryFn: () => getSystemEmailFn({ data: { emailType: type, language } }),
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 60,
    refetchOnWindowFocus: false,
  };
}

export function organisationEmailQueryOptions(orgId: number, type: EmailType, language: EmailLanguage) {
  return {
    queryKey: ["email", "organisation", orgId, type, language],
    queryFn: () => getOrganisationEmailFn({ data: { orgId, emailType: type, language } }),
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 60,
    refetchOnWindowFocus: false,
  };
}

export const emailTypesQueryOptions = {
  queryKey: ["email-types"],
  queryFn: () => getEmailTypesFn(),
  staleTime: 1000 * 60 * 60,
  gcTime: 1000 * 60 * 60 * 24,
  refetchOnWindowFocus: false,
  refetchOnMount: false,
};
